import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRecordFeedback, type MonitorMatch } from "@/monitors/api";

interface Props {
  match: MonitorMatch;
  monitorName: string;
  size?: "sm" | "md";
  showWhy?: boolean;
  showThumbs?: boolean;
}

export function MatchCard({
  match,
  monitorName,
  size = "md",
  showWhy = true,
  showThumbs = false,
}: Props) {
  const navigate = useNavigate();
  const feedback = useRecordFeedback();

  const compact = size === "sm";

  return (
    <Card data-size={size} className={compact ? "border-l-4" : ""}>
      <CardContent className={compact ? "p-3" : "p-4"}>
        {monitorName && (
          <div className="text-xs text-muted-foreground mb-1">{monitorName}</div>
        )}
        <div className={`font-medium ${compact ? "text-sm" : "text-base"}`}>
          {match.cluster_snapshot.title ?? "Cluster"}
        </div>
        {showWhy && match.verifier_reason && (
          <div className="text-xs text-muted-foreground italic mt-2">
            Why: {match.verifier_reason}
          </div>
        )}
        <div
          className={`flex items-center justify-between ${compact ? "mt-2" : "mt-3"} text-xs`}
        >
          <span className="text-muted-foreground">
            {match.confidence !== null
              ? `conf ${match.confidence.toFixed(2)}`
              : `sim ${match.similarity_score.toFixed(2)}`}{" "}
            · {tryRelative(match.detected_at)}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/clusters/${match.cluster_id}`)}
            >
              Open <ExternalLink className="ms-1 h-3 w-3" />
            </Button>
            {showThumbs && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={match.user_feedback === "correct"}
                  onClick={() =>
                    feedback.mutate({ matchId: match.id, value: "correct" })
                  }
                  aria-label="Mark correct"
                >
                  <ThumbsUp className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={match.user_feedback === "wrong"}
                  onClick={() =>
                    feedback.mutate({ matchId: match.id, value: "wrong" })
                  }
                  aria-label="Mark wrong"
                >
                  <ThumbsDown className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function tryRelative(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
